import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { ModalShellComponent } from '../modal-shell/modal-shell.component';
import { LoaderComponent } from '../../common/loader/loader.component';
import { TenantsService } from '../../services/tenants.service';
import { TenantUsersService } from '../../services/tenant-users.service';
import { ToastrService } from '../../services/toastr.service';
import { TenantUser, UserServiceAssignment } from '../../models/tenant-user.model';
import { ApiErrorResponse } from '../../models/auth.model';

interface ServiceRow {
  tenantServiceId: number;
  serviceName: string;
  assignmentId: number | null;
  initialActive: boolean;
  checked: boolean;
}

@Component({
  selector: 'app-manage-user-services-modal',
  imports: [ModalShellComponent, LoaderComponent],
  templateUrl: './manage-user-services.component.html',
  styleUrl: './manage-user-services.component.scss'
})
export class ManageUserServicesModalComponent implements OnInit {
  @Input() tenantUid = '';
  @Input() user: TenantUser | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  loading = true;
  saving = false;
  rows: ServiceRow[] = [];

  constructor(
    private tenantsService: TenantsService,
    private tenantUsersService: TenantUsersService,
    private toastrService: ToastrService
  ) { }

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    if (!this.tenantUid) {
      this.loading = false;
      this.rows = [];
      return;
    }

    this.loading = true;

    this.tenantsService.getTenantServices(this.tenantUid).subscribe({
      next: (tenantServices) => {
        const assignments = this.user?.service_assignments ?? [];
        this.rows = tenantServices
          .filter(ts => ts.is_active)
          .map(ts => {
            const assignment = assignments.find(a => a.tenant_service === ts.id) ?? null;
            const active = assignment?.is_active ?? false;
            return {
              tenantServiceId: ts.id,
              serviceName: ts.service_name,
              assignmentId: assignment?.id ?? null,
              initialActive: active,
              checked: active
            };
          });
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.rows = [];
        this.toastrService.error(this.resolveError(err, 'Unable to load services for this tenant.'));
      }
    });
  }

  toggle(row: ServiceRow): void {
    row.checked = !row.checked;
  }

  get hasChanges(): boolean {
    return this.rows.some(r => r.checked !== r.initialActive);
  }

  close(): void {
    if (this.saving) {
      return;
    }
    this.closed.emit();
  }

  save(): void {
    if (!this.user || this.saving || !this.hasChanges) {
      return;
    }

    const changed = this.rows.filter(r => r.checked !== r.initialActive);
    const calls: Observable<unknown>[] = changed.map(row => {
      if (row.assignmentId) {
        return this.tenantUsersService.updateServiceAssignment(row.assignmentId, { is_active: row.checked });
      }
      // Turning a service on for the first time — no existing assignment row to PATCH, so create one.
      return this.tenantUsersService.createServiceAssignment({
        user_profile: this.user!.email,
        tenant_service: row.tenantServiceId,
        is_active: row.checked
      });
    });

    this.saving = true;

    forkJoin(calls).subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit();
        this.closed.emit();
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.toastrService.error(this.resolveError(err, 'Unable to update service assignments.'));
      }
    });
  }

  private resolveError(err: HttpErrorResponse, fallback: string): string {
    const body = err?.error as ApiErrorResponse | undefined;
    return body?.detail ?? body?.error?.message ?? fallback;
  }
}
